import {
  CollaborationV1ExternalDocumentSourceType,
  CollaborationV1ProviderDocumentPage,
  CollaborationV1SearchProvider,
  CollaborationV1SearchProviderDocumentsResponse,
  CollaborationV1SearchProviderDocumentsResponseResult,
  ErrorCode,
  useSearchProviderDocumentsQuery,
} from '@spotify/confidence-graphql';

const SOURCE_TYPE_TO_SEARCH_PROVIDER: Record<
  CollaborationV1ExternalDocumentSourceType,
  CollaborationV1SearchProvider
> = {
  [CollaborationV1ExternalDocumentSourceType.GoogleDocs]:
    CollaborationV1SearchProvider.GoogleDrive,
  [CollaborationV1ExternalDocumentSourceType.GoogleSlides]:
    CollaborationV1SearchProvider.GoogleDrive,
  [CollaborationV1ExternalDocumentSourceType.Notion]:
    CollaborationV1SearchProvider.Notion,
};

export type UseSearchProviderDocumentsOptions = {
  sourceType: CollaborationV1ExternalDocumentSourceType;
  query?: string;
  pageSize?: number;
  skip?: boolean;
};

export type UseSearchProviderDocumentsResult = {
  pages: CollaborationV1ProviderDocumentPage[];
  nextPageToken?: string | null;
  isLoading: boolean;
  error?: string;
  fetchMore: () => void;
  refetch: () => void;
};

type ProviderErrorResponse = {
  __typename: 'Error';
  message?: string | null;
  code: ErrorCode;
};

function isSuccessResponse(
  result:
    | CollaborationV1SearchProviderDocumentsResponseResult
    | null
    | undefined,
): result is CollaborationV1SearchProviderDocumentsResponse {
  return !!result && 'pages' in result;
}

function isErrorResponse(
  result:
    | CollaborationV1SearchProviderDocumentsResponseResult
    | null
    | undefined,
): result is ProviderErrorResponse {
  return !!result && '__typename' in result && result.__typename === 'Error';
}

function getProviderError(
  result:
    | CollaborationV1SearchProviderDocumentsResponseResult
    | null
    | undefined,
): string | undefined {
  if (!isErrorResponse(result)) return undefined;
  return (
    result.message ??
    result.code?.replaceAll('_', ' ') ??
    'Something went wrong'
  );
}

export const useSearchProviderDocuments = (
  options: UseSearchProviderDocumentsOptions,
): UseSearchProviderDocumentsResult => {
  const { sourceType, query, pageSize = 20, skip } = options;
  const provider = SOURCE_TYPE_TO_SEARCH_PROVIDER[sourceType];

  const { data, loading, error, fetchMore, refetch } =
    useSearchProviderDocumentsQuery({
      variables: { provider, query, pageSize },
      skip: skip || query === undefined,
      fetchPolicy: 'network-only',
    });

  const result = data?.searchProviderDocuments;
  const pages = isSuccessResponse(result) ? result.pages : [];
  const nextPageToken = isSuccessResponse(result)
    ? result.nextPageToken
    : undefined;
  const providerError = getProviderError(result);

  const handleFetchMore = () => {
    if (!nextPageToken) return;
    void fetchMore({
      variables: {
        provider,
        query,
        pageSize,
        pageToken: nextPageToken,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        const previous = previousResult.searchProviderDocuments;
        const next = fetchMoreResult.searchProviderDocuments;
        if (!isSuccessResponse(previous) || !isSuccessResponse(next)) {
          return fetchMoreResult;
        }
        return {
          searchProviderDocuments: {
            ...next,
            pages: [...previous.pages, ...next.pages],
          },
        };
      },
    });
  };

  return {
    pages,
    nextPageToken,
    isLoading: loading,
    error: providerError ?? error?.message,
    fetchMore: handleFetchMore,
    refetch: () => void refetch(),
  };
};
